import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {IntermediaryEntity} from '../services/api.bokkz.services';
import {BokkzAlgDataService, BokkzAlgLogic} from '@snelstart-ventures/fiscaal/domain-logic';
import {CommonModule} from '@angular/common';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {firstValueFrom, startWith, Subject, takeUntil} from 'rxjs';
import {SharedUiStepperModule, StepperItem} from '@snelstart/ui-stepper';
import {BokkzAlgemeenClient, Credentials} from '../services/api.bokkz.algemeen';
import {toSignal} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-intermediairy',
  templateUrl: './intermediary.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    SharedUiIconModule,
    SharedUiAlertModule,
    SharedUiStepperModule,
    CommonModule,
  ],
  styleUrl: './intermediary.component.css'
})
export class IntermediaryComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public dialogVisible = false;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();
  private destroy$ = new Subject<void>();
  public intermediaryFormGroup = this.fb.group<IntermediaryEntity>({});
  public intermediaryDisplayFormGroup: FormGroup = this.fb.group({});
  readonly credentialsFormgroup = this.fb.group<Credentials>({
    id: '',
    sessionId: '',
    succes: false,
    twoFactor: false,
    active: false,
    loggedIn: false,
    code: null as unknown as string
  });

  public uploadSuccesvol = true;
  public fileName = '';
  public pkiUpload = {} as FormGroup;
  public selectedTab = 0;
  public focusedElementId: string | null = null;
  public formStructure: any;
  public isValidBeconId = true;

  readonly active = toSignal(
    this.credentialsFormgroup.controls.active.valueChanges.pipe(
      startWith(this.credentialsFormgroup.controls.active.value)
    )
  );

  readonly id = toSignal(
    this.credentialsFormgroup.controls.id.valueChanges.pipe(
      startWith(this.credentialsFormgroup.controls.id.value)
    )
  );


  public optionsJaNeeSba: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja, ophalen via SnelStart Fiscaal'},
    {value: false, label: 'Nee, niet ophalen via SnelStart Fiscaal'}
  ];

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public stepperItems: StepperItem[] = [
    {label: 'Wachtwoord invullen', state: 'active'},
    {label: 'Certificaat uploaden', state: 'toDo'},
    {
      label: 'Serviceberichten activeren',
      state: this.intermediaryFormGroup.get('hasBerichten')?.getRawValue() === undefined ? 'toDo' : 'done'
    }
  ];

  constructor(
    private algLogic: BokkzAlgLogic,
    private algDataService: BokkzAlgDataService,
    private bokkzAlgemeenClient: BokkzAlgemeenClient,
    public fb: FormBuilder
  ) {
    this.intermediaryDisplayFormGroup = this.fb.group({
      sstPrimaryAdminName: null as unknown as string
    });
    this.intermediaryFormGroup = this.fb.group<IntermediaryEntity>({
      id: null as unknown as number,
      sstPrimaryAdminId: null as unknown as string,
      licenceName: null as unknown as string,
      userName: null as unknown as string,
      officeName: null as unknown as string,
      officeEmail: null as unknown as string,
      officePhone: null as unknown as string,
      officeLogo: null as unknown as string[],
      taxId: null as unknown as string,
      beconId: null as unknown as string,
      gemachtigdeId: null as unknown as string,
      hasBerichten: null as unknown as boolean,
      pkiCertificate: null as unknown as string[],
      certValidUntil: null as unknown as Date,
      getMachtigingToestanden: null as unknown as boolean,
      hasIntro: null as unknown as boolean,
      doScraping: null as unknown as boolean,
      software: null as unknown as string,
      softwareUserName: null as unknown as string,
      softwarePassword: null as unknown as string,
      softwareKey: null as unknown as string,
      hasTwofactor: null as unknown as boolean,
      hasMigrated: false
    });
    this.pkiUpload = this.fb.group({
      newCertUpload: [''],
      pkiPassword: ['', Validators.required],
      file: [''],
      attachments: this.fb.array([])
    });
  }

  ngOnInit() {
    this.intermediaryFormGroup.reset();
    this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((intermediaryEntity) => {
      this.intermediaryFormGroup.patchValue(intermediaryEntity, {emitEvent: false});
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['dialogVisible']?.currentValue === true) {
      const admins = await firstValueFrom(this.algLogic.getAllAdministrations());
      const matchedAdmin = admins.find(
        (admin) => String(admin.id) === String(this.intermediaryFormGroup.getRawValue().sstPrimaryAdminId)
      );
      this.intermediaryDisplayFormGroup.patchValue(
        {sstPrimaryAdminName: matchedAdmin ? matchedAdmin.name : this.intermediaryFormGroup.getRawValue().sstPrimaryAdminId},
        {emitEvent: false}
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  activateStepperItems(index: number): void {
    if (this.stepperItems[index - 1]) {
      this.stepperItems[index - 1].state = 'done';
    }
    this.stepperItems[index].state = 'active';
    if (this.stepperItems.length === index) {
      this.stepperItems[index].state = 'done';
    }
  }

  onSelectTab(number: number) {
    this.selectedTab = number;
    if (number === 3) {
      this.bokkzAlgemeenClient.getLoginStatus().subscribe({
        next: (credentialsResponse) => {
          this.credentialsFormgroup.patchValue(credentialsResponse);
        },
        error: () => {
        }
      });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const formData = new FormData();
    this.fileName = file.name;
    formData.append('password', String(this.pkiUpload.controls['pkiPassword'].value));
    formData.append('pki', file);
    this.algLogic.postPkiCertificaat(formData).subscribe((success) => {
      if (success) {
        this.activateStepperItems(2);
        this.uploadSuccesvol = success;
      } else {
        this.uploadSuccesvol = false;
      }
    });
  }

  validateBeconnummer(): boolean {
    this.isValidBeconId = false;
    const value = this.intermediaryFormGroup.get('beconId')?.getRawValue();

    if (value == null || value === '') {
      return true;
    }

    if (value.length !== 6) {
      this.isValidBeconId = false;
      return false;
    }

    const x = value;

    const a = parseInt(x.charAt(0));
    const b = parseInt(x.charAt(1));
    const c = parseInt(x.charAt(2));
    const d = parseInt(x.charAt(3));
    const e = parseInt(x.charAt(4));
    const f = parseInt(x.charAt(5));

    const getalA = a * 6;
    const getalB = b * 5;
    const getalC = c * 4;
    const getalD = d * 3;
    const getalE = e * 2;
    const getalF = f * -1;

    const uitkomst = getalA + getalB + getalC + getalD + getalE + getalF;
    this.isValidBeconId = uitkomst % 11 === 0;
    return uitkomst % 11 === 0;
  }

  postOneIntermediary(scraping: boolean) {
    this.intermediaryFormGroup.patchValue({doScraping: scraping});
    this.algLogic.postOneIntermediary(this.intermediaryFormGroup.getRawValue() as IntermediaryEntity);
    this.dialogVisibleChange.emit(false);
  }
}
